import { Text } from '@dixa/lib-shared-habla';
import { NotificationPayloadT } from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import MomentTimezone from 'moment-timezone';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { put } from 'redux-saga/effects';

import { notificationActions } from '../../notifications/actions/notification';
import { actions as conversationActions } from '../actions/conversation';

export const { conversationCompleteAcw, conversationExtendAcw } =
  conversationActions;

const { notificationRemoveById } = notificationActions;

export const acwNotificationType = 'acw';

const extendDuration = 30;

/** Returns the ACW (wrap up time) notification type by conversation id. */
export function getAcwNotificationIdByCsid(csid: number | string): string {
  return `${acwNotificationType}_${csid}`;
}

/** Gets the conversation id from the ACW notification id. */
export function getCsidFromAcwNotificationId(id: string): number | undefined {
  const csid = parseInt(id.replace(`${acwNotificationType}_`, ''));
  return isNaN(csid) ? undefined : csid;
}

type ExpirationSeconds = {
  seconds: number;
};

type ExpirationTime = {
  time: string | null;
};

type Expiration = ExpirationSeconds | ExpirationTime;

function getExpirationMilliseconds(expiration: Expiration): number | null {
  const isSeconds = ('seconds' satisfies keyof ExpirationSeconds) in expiration;
  const isTime = ('time' satisfies keyof ExpirationTime) in expiration;

  if (isSeconds && expiration.seconds >= 0) {
    return expiration.seconds * 1000;
  }

  if (isTime && expiration.time) {
    return MomentTimezone(expiration.time).diff(MomentTimezone());
  }

  return null;
}

export function getAcwNotification({
  csid,
  text = 'You are now in wrap-up time in conversation',
  expiration,
}: {
  csid: number | string;
  text?: string | ReactNode;
  expiration: Expiration;
}): NotificationPayloadT {
  const notificationId = getAcwNotificationIdByCsid(csid);
  const expirationMilliseconds = getExpirationMilliseconds(expiration);
  const isEndWrapUpBlocked = expirationMilliseconds === null;

  if (
    typeof expirationMilliseconds === 'number' &&
    expirationMilliseconds < 0
  ) {
    Sentry.captureMessage('Illegal expiration time for ACW notification', {
      level: 'warning',
      extra: {
        csid,
        expiration,
        expirationMilliseconds,
      },
      tags: {
        team: SentryTeam.Conversations,
      },
    });
  }

  return {
    id: notificationId,
    type: acwNotificationType,
    title: `Wrap-up time`,
    text: (
      <Text.Paragraph>
        {text} <Link to={`/conversation/${csid}`}>#{csid}</Link>
      </Text.Paragraph>
    ),
    icon: 'clock-one',
    time: expirationMilliseconds ?? undefined,
    closeAction: function* () {
      yield put(notificationRemoveById({ id: notificationId }));
      if (!isEndWrapUpBlocked) {
        yield put(conversationCompleteAcw({ csid }));
      }
    },
    mainAction: {
      navigate: `/conversation/${csid}`,
    },
    banner: isEndWrapUpBlocked
      ? {
          description: (
            <span>
              Wrap-up time can only end when the missing required attributes are
              filled in.
            </span>
          ),
          variant: 'warning',
        }
      : undefined,
    leftAction: {
      text: `Extend with ${MomentTimezone(extendDuration * 1000)
        .utc()
        .format('mm:ss')}`,
      hidden: isEndWrapUpBlocked,
      action: function* () {
        try {
          if (!isEndWrapUpBlocked) {
            yield put(
              conversationExtendAcw({ csid, duration: extendDuration }),
            );
          }
        } catch (exception) {
          console.warn(exception);
        }
      },
    },
    rightAction: {
      text: 'End',
      hidden: isEndWrapUpBlocked,
      action: function* () {
        yield put(conversationCompleteAcw({ csid }));
        yield put(
          notificationRemoveById({
            id: notificationId,
          }),
        );
      },
    },
  };
}
